import { CheckSquare, Calendar, Mail, Phone } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'
import { GRACKLE_COLORS, CATEGORICAL, ACTIVITY_TYPE_COLORS } from '@/lib/theme'

export default function ActivitiesSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        In high-volume sales, the details make the difference. Activities — tasks, events, emails,
        and calls — are how you track every touchpoint with a prospect or customer. Logging them
        consistently means nothing slips through the cracks, and your team always knows the full
        story behind a deal.
      </p>

      <section id="tasks" aria-labelledby="tasks-heading">
        <div className="flex items-center gap-2 mb-2">
          <CheckSquare
            className="h-4 w-4"
            style={{ color: ACTIVITY_TYPE_COLORS.Task.solid }}
            aria-hidden="true"
          />
          <h3 id="tasks-heading" className="font-semibold">
            Tasks
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Tasks are your to-do list inside the CRM. Use them to track follow-up calls, proposal
          deadlines, internal action items — anything that needs to get done.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">To create a task:</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Go to <span className="font-medium">Activities &rarr; Tasks</span> and click{' '}
            <span className="font-medium">New Task</span>
          </li>
          <li>Enter a subject (e.g., "Follow up with Acme Corp on pricing")</li>
          <li>Set a due date and priority</li>
          <li>Assign it to yourself or a teammate</li>
          <li>Link it to an account, contact, lead, or opportunity for context</li>
          <li>
            Click <span className="font-medium">Save</span>
          </li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          You can also create tasks and events directly from the Activity tab on Accounts, Contacts,
          Leads, and Opportunities. The owner defaults to you automatically, and the related record
          is linked for you — no extra clicks needed.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Key features:</span> Sort tasks by due date, priority, or
          status. Filter by date range to focus on this week. Use bulk actions to reassign or close
          multiple tasks at once.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Task due date reminders:</span> The system sends automatic
          daily notifications for tasks that are due tomorrow, due today, or overdue. These appear
          as a calendar icon in the notification bell. Reminders are also sent via email and
          optionally to Google Chat if configured.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Tasks on tickets:</span> Support users can create tasks
          directly from the Tasks tab on a ticket detail page. See{' '}
          <a
            href="#ticket-tasks"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Tasks on Tickets
          </a>{' '}
          under Ticketing for details.
        </p>
      </section>
      <Separator />

      <section id="events" aria-labelledby="events-heading">
        <div className="flex items-center gap-2 mb-2">
          <Calendar
            className="h-4 w-4"
            style={{ color: ACTIVITY_TYPE_COLORS.Event.solid }}
            aria-hidden="true"
          />
          <h3 id="events-heading" className="font-semibold">
            Events
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Events are meetings, calls, and appointments. You can view them in a{' '}
          <span className="font-medium">calendar view</span> (click any day to create an event) or
          switch to <span className="font-medium">list view</span> for quick scanning and filtering.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Google Calendar integration:</span> Connect your Google
          account in Settings to sync events both ways. You can also schedule Google Meet meetings
          directly from Account, Contact, and Opportunity detail pages — the meeting link is created
          automatically.
        </p>
        <ProTip>
          <span className="font-medium">Before a call:</span> Open the related account or contact to
          review recent activity and notes. Being prepared builds trust and shortens sales cycles.
        </ProTip>
      </section>
      <Separator />

      <section id="emails" aria-labelledby="emails-heading">
        <div className="flex items-center gap-2 mb-2">
          <Mail
            className="h-4 w-4"
            style={{ color: ACTIVITY_TYPE_COLORS.Email.solid }}
            aria-hidden="true"
          />
          <h3 id="emails-heading" className="font-semibold">
            Emails
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          When Gmail sync is enabled, your sent and received emails appear automatically in the CRM.
          They are linked to the relevant accounts and contacts based on email addresses, giving you
          a complete communication history without any manual data entry. Internal emails between
          grackledocs.com team members are automatically filtered out — only emails involving
          external contacts or leads are synced.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Connect first:</span> Gmail sync only runs once you have
          connected your own Google account — it is a one-time manual step under{' '}
          <a
            href="/help#google-integration"
            className="text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            Settings &rarr; Google
          </a>
          . When you first connect, the CRM imports roughly the last 60 days of email history (your
          administrator can adjust this window); after that, new mail syncs automatically.
        </p>

        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Compose and send emails:</span> You can send emails directly
          from the CRM using the Gmail API. Click the <span className="font-medium">Compose</span>{' '}
          button on the Emails page or from the activity tab on any Account, Contact, Lead, or
          Opportunity detail page.
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            Click <span className="font-medium">Compose Email</span>
          </li>
          <li>Enter the recipient in the To field</li>
          <li>
            Optionally click <span className="font-medium">CC/BCC</span> to add additional
            recipients
          </li>
          <li>Enter a subject and message body</li>
          <li>
            Click <span className="font-medium">Send</span>
          </li>
        </ol>

        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Reply to emails:</span> Click the reply button on any email
          in the activity timeline. The recipient and subject line (with &ldquo;Re:&rdquo;) are
          pre-filled automatically, and the reply is threaded with the original conversation.
        </p>

        <div
          className="rounded-md border p-3 mb-2"
          style={{
            backgroundColor: CATEGORICAL.navy.tint,
            borderColor: CATEGORICAL.navy.solid,
            color: GRACKLE_COLORS.prussianBlue,
          }}
          role="note"
        >
          <p className="text-sm">
            <span className="font-medium">Beta:</span> Email sending is currently limited to{' '}
            <strong>@grackledocs.com</strong> addresses. Emails to other domains are saved as drafts
            in your Gmail account.
          </p>
        </div>

        <p className="text-sm text-muted-foreground">
          View related emails on any account, contact, or opportunity detail page under the{' '}
          <span className="font-medium">Emails</span> tab.
        </p>
      </section>
      <Separator />

      <section id="call-logging" aria-labelledby="call-logging-heading">
        <div className="flex items-center gap-2 mb-2">
          <Phone
            className="h-4 w-4"
            style={{ color: ACTIVITY_TYPE_COLORS.Call.solid }}
            aria-hidden="true"
          />
          <h3 id="call-logging-heading" className="font-semibold">
            Call Logging
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Log every phone call — outbound and inbound — directly in the CRM. Calls appear in the
          activity timeline alongside tasks, events, and emails, giving you a complete communication
          history for every contact, lead, and account.
        </p>

        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Click-to-call (outbound):</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>Click any phone number on a Contact, Lead, Account, or Opportunity detail page</li>
          <li>Your phone app (e.g., Zoiper) opens and dials the number automatically</li>
          <li>A call logging dialog appears with a live timer tracking call duration</li>
          <li>
            When the call ends, select a <span className="font-medium">disposition</span>{' '}
            (Connected, No Answer, Left Voicemail, Busy, Wrong Number, Disconnected, Gatekeeper)
          </li>
          <li>
            Add notes and an optional subject, then click{' '}
            <span className="font-medium">End Call &amp; Log</span>
          </li>
        </ol>

        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Log incoming call:</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            Click the <span className="font-medium">phone icon</span> in the top navigation bar
            (next to the notification bell)
          </li>
          <li>
            Type the caller's phone number — the CRM searches contacts, leads, and accounts
            automatically
          </li>
          <li>Select the matching record, fill in disposition, duration, and notes</li>
          <li>
            Click <span className="font-medium">Log Call</span>
          </li>
        </ol>

        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Where calls appear:</span>
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>
            <span className="font-medium">Activity Timeline</span> — Calls show with an orange icon
            and display direction (Inbound/Outbound) and duration
          </li>
          <li>
            <span className="font-medium">Dashboard</span> — The "Calls Today" card in the Quick
            Stats row shows your daily and weekly call counts
          </li>
        </ul>

        <ProTip>
          Even without a VoIP softphone, you can log calls manually. Click any phone number, dismiss
          the phone app if it opens, and use the dialog to record call details after dialing from
          your desk phone or mobile.
        </ProTip>
      </section>
    </>
  )
}
